import { Facebook, Instagram, Linkedin, Twitter, Mail, Phone } from "lucide-react";
import { useT } from "../../i18n/I18nContext";
import { WHATSAPP_NUMBERS, EMAIL } from "./contacts";

const FOOTER_LINK_IDS = ["services", "portfolio", "pricing", "insights", "contact"];

export const Footer = () => {
  const t = useT();
  const linkLabels = {
    services: t.nav.services,
    portfolio: t.nav.portfolio,
    pricing: t.nav.pricing,
    insights: t.nav.insights,
    contact: t.nav.contact,
  };

  const handleAnchor = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0A2342] text-slate-300 relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
        aria-hidden="true"
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <a
              href="#home"
              onClick={(e) => handleAnchor(e, "home")}
              className="flex items-center gap-2.5"
              data-testid="footer-logo"
            >
              <span className="relative inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white overflow-hidden">
                <img
                  src="/uv-mark-64.png"
                  alt="UrbanVista Web"
                  width="44"
                  height="44"
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </span>
              <span className="font-bold text-white tracking-tight text-xl leading-none">
                UrbanVista Web
              </span>
            </a>
            <p className="mt-5 text-base text-slate-300 max-w-md leading-relaxed">
              {t.footer.brandStatement}
            </p>

            <div className="mt-7 flex items-center gap-3">
              {[
                { Icon: Facebook, label: "Facebook" },
                { Icon: Instagram, label: "Instagram" },
                { Icon: Linkedin, label: "LinkedIn" },
                { Icon: Twitter, label: "X / Twitter" },
              ].map(({ Icon, label }) => (
                <a
                  key={label}
                  href="#"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase().replace(/\s|\//g, "-")}`}
                  className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
              {t.footer.navigate}
            </p>
            <ul className="mt-5 space-y-3">
              {FOOTER_LINK_IDS.map((id) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    onClick={(e) => handleAnchor(e, id)}
                    data-testid={`footer-link-${id}`}
                    className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
                  >
                    {linkLabels[id]}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
              {t.footer.getInTouch}
            </p>
            <ul className="mt-5 space-y-3">
              <li>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="footer-email"
                  className="inline-flex items-center gap-3 text-sm text-slate-300 hover:text-white transition-colors break-all"
                >
                  <Mail className="w-4 h-4 shrink-0" />
                  {EMAIL}
                </a>
              </li>
              {WHATSAPP_NUMBERS.map((n, idx) => (
                <li key={n.digits}>
                  <a
                    href={`https://wa.me/${n.digits}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-phone-${idx}`}
                    className="inline-flex items-center gap-3 text-sm text-slate-300 hover:text-white transition-colors"
                  >
                    <Phone className="w-4 h-4 shrink-0" />
                    {n.display}
                  </a>
                </li>
              ))}
            </ul>

            <a
              href="#contact"
              onClick={(e) => handleAnchor(e, "contact")}
              data-testid="footer-cta"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-white text-[#0A2342] px-5 py-2.5 text-sm font-semibold hover:bg-slate-100 transition-colors"
            >
              {t.footer.cta}
            </a>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-400">{t.footer.rights}</p>
          <p className="text-xs text-slate-400">{t.footer.tagline}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
